import { useCallback, useRef } from "react";
import { trainingAPI } from "@api/TrainingAPI";
import { serializeDashboardSpec } from "../Contexts/serializeSpec";
import { canonicalJson } from "./CompareDiff";
import type { DashboardState } from "../Contexts/dashboardState";
import type { BackendDashboardSpec } from "../Processing/types";
import type {
  SandboxAIMessage,
  SandboxAIResponse,
  SandboxAIStatus,
  TurnSnapshot,
} from "./types";

// Dependencies the send/cancel flow reaches into. Read through a ref so the async
// resolve/reject handlers always see the latest state without re-creating callbacks.
export interface SandboxAIChatDeps {
  getDashboardState: () => DashboardState;
  applyServerSpec: (spec: BackendDashboardSpec) => void;
  renameDashboard: (name: string) => void | Promise<void>;
  getConversationId: () => number | string | null;
  setConversationId: (id: number | string | null) => void;
  pushMessage: (message: SandboxAIMessage) => void;
  setStatus: (status: SandboxAIStatus) => void;
  setTurnSnapshot: (snapshot: TurnSnapshot | null) => void;
}

const newId = (): string =>
  `sbxai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// The FE's default dashboard names — only auto-rename over one of these, never a real name.
const UNNAMED = new Set(["", "unnamed", "untitled", "new dashboard"]);
const isUnnamed = (name: string | undefined): boolean =>
  UNNAMED.has((name ?? "").trim().toLowerCase());

const deepCopy = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

export function useSandboxAIChat(deps: SandboxAIChatDeps): {
  sendMessage: (text: string) => void;
  cancelTurn: () => void;
  retryLast: () => void;
} {
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const abortRef = useRef<AbortController | null>(null);
  const lastInputRef = useRef<string>("");

  const sendMessage = useCallback((rawText: string) => {
    const d = depsRef.current;
    const text = rawText.trim();
    if (!text) return;
    lastInputRef.current = text;

    // Starting a new message discards any pending revert snapshot (Keep is implicit).
    d.setTurnSnapshot(null);

    const dashboardState = d.getDashboardState();
    const clientSpec = serializeDashboardSpec(dashboardState);
    const dashboardId = dashboardState.dashboardId;

    d.pushMessage({ id: newId(), role: "user", content: text, created: Date.now() });
    d.setStatus("working");

    const controller = new AbortController();
    abortRef.current = controller;

    trainingAPI
      .postSandboxAIChat({
        dashboardId,
        input: text,
        conversationId: d.getConversationId(),
        clientSpec,
        signal: controller.signal,
      })
      .then((res: SandboxAIResponse) => {
        const cur = depsRef.current;
        if (controller.signal.aborted) return;
        if (res.conversation_id != null) cur.setConversationId(res.conversation_id);

        // TEMP(debug): stash the raw backend response for the copy button.
        const raw = JSON.stringify(res, null, 2);

        if (res.error) {
          cur.pushMessage({
            id: newId(), role: "assistant",
            content: res.error || "Something went wrong.",
            error: true, raw, created: Date.now(),
          });
          cur.setStatus("error");
          return;
        }

        // TEMP(debug): canonical before/after for the Compare diff button. "Before" is
        // the clientSpec this turn was seeded from; "after" is the committed dashboard.
        const committed = Boolean(res.committed && res.dashboard);
        cur.pushMessage({
          id: newId(), role: "assistant",
          content: res.text || "", ops: res.ops ?? [],
          raw,
          ...(committed && {
            diffBefore: canonicalJson(clientSpec),
            diffAfter: canonicalJson(res.dashboard),
          }),
          created: Date.now(),
        });

        // Snapshot the CURRENT dashboard state before applying the committed spec,
        // so Undo can restore it in one full-state replacement.
        if (res.committed && res.dashboard) {
          const live = cur.getDashboardState();
          const snapshot: TurnSnapshot = {
            widgets: deepCopy(live.widgets),
            layout: deepCopy(live.layout),
          };
          cur.applyServerSpec(res.dashboard);
          cur.setTurnSnapshot(snapshot);
          // Apply the AI's chosen name only if the board is still on a default name —
          // never clobber a name the coach has set.
          if (res.dashboard_name && isUnnamed(live.name)) {
            Promise.resolve(cur.renameDashboard(res.dashboard_name)).catch(() => {});
          }
        }
        cur.setStatus("idle");
      })
      .catch((err: unknown) => {
        const cur = depsRef.current;
        const aborted =
          controller.signal.aborted || (err as { name?: string })?.name === "AbortError";
        if (aborted) {
          cur.pushMessage({
            id: newId(), role: "assistant",
            content: "Turn cancelled.", muted: true, created: Date.now(),
          });
          cur.setStatus("idle");
          return;
        }
        cur.pushMessage({
          id: newId(), role: "assistant",
          content: (err as Error)?.message || "Something went wrong. Please try again.",
          error: true, created: Date.now(),
        });
        cur.setStatus("error");
      })
      .finally(() => {
        if (abortRef.current === controller) abortRef.current = null;
      });
  }, []);

  const cancelTurn = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const retryLast = useCallback(() => {
    if (lastInputRef.current) sendMessage(lastInputRef.current);
  }, [sendMessage]);

  return { sendMessage, cancelTurn, retryLast };
}
